/**
 * Period progress — track completion progress for x_per_week / x_per_month schedules.
 */

import { compareDates, daysBetween } from "./day-boundary";

import type { CompletionRecord, PeriodBounds } from "./types";

export interface PeriodProgress {
  completed: number;
  required: number;
  onTrack: boolean;
}

/**
 * Calculate progress within a period for period-based schedules.
 *
 * @param required - Target count for the period (e.g. 3 per week)
 * @param completions - All completion records for this habit
 * @param periodBounds - Start/end of the current period
 * @param today - The current effective date (YYYY-MM-DD)
 */
export function getPeriodProgress(
  required: number,
  completions: CompletionRecord[],
  periodBounds: PeriodBounds,
  today: string,
): PeriodProgress {
  // Count done/backfill completions within the period
  let completed = 0;
  for (const c of completions) {
    if (c.type === "skip") continue;
    if (
      compareDates(c.effective_date, periodBounds.start) >= 0 &&
      compareDates(c.effective_date, periodBounds.end) <= 0
    ) {
      completed++;
    }
  }

  // Calculate on-track status
  // At the current pace, will we meet the target by period end?
  const remaining = required - completed;
  if (remaining <= 0) {
    return { completed, required, onTrack: true };
  }

  // Clamp today to period bounds for the calculation
  const effectiveToday = compareDates(today, periodBounds.end) > 0 ? periodBounds.end : today;

  const daysLeft = daysBetween(effectiveToday, periodBounds.end);
  // Include today as a remaining day (can still complete today)
  const daysAvailable = daysLeft + 1;

  return {
    completed,
    required,
    onTrack: daysAvailable >= remaining,
  };
}
